import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmDialogComponent } from '../../dialog/confirm-dialog/confirm-dialog.component';
import { LessonsScheduleEditComponent } from '../lessons-schedule-edit/lessons-schedule-edit.component';
import { LessonSchedule } from 'src/app/classes/LessonSchedule';
import { User } from 'src/app/classes/User';
import { UserService } from 'src/app/services/user/user.service';
import { LessonsScheduleService } from 'src/app/services/lessons-schedule.service';
import { Room } from 'src/app/classes/Room';
import { RoomService } from 'src/app/services/room.service';
import { Lesson, LessonGroup, WeekDays } from 'src/app/components/lessons-schedule/lessons-schedule-list/types';

enum ViewModes {
  LESSONS,
  COURSES
}

@Component({
  selector: 'app-lessons-schedule-list',
  styleUrls: ['./lessons-schedule-list.component.scss'],
  templateUrl: './lessons-schedule-list.component.html',
})
export class LessonsScheduleListComponent implements OnInit {

  public lessonSchedules: LessonSchedule[];
  public rooms: Room[];
  public teachers: User[];
  public errorMessage: string;
  public teachersGroup: FormGroup;
  public loading = false;
  public lessonsScheduleByDates: LessonGroup[] = [];
  public startDate;
  public endDate;
  public dateStartDate;
  public dateEndDate;
  public radioModel = 'byDate';
  public weekDates: Date[] = [];

  private weekDays = WeekDays;
  private currentDialog = null;
  public viewModes = ViewModes;
  public viewMode = ViewModes.LESSONS;
  public rawEvents: any = [];

  constructor(
    private userService: UserService,
    private roomService: RoomService,
    private scheduleService: LessonsScheduleService,
    private modalService: NgbModal,
    private formBuilder: FormBuilder
  ) {
  }

  public findRoomById(id: number): Room {
    return this.rooms.find((value) => value.id === id);
  }

  public ngOnInit(): void {
    this.roomService.getAll()
      .subscribe({
        next: (response: Room[]) => this.rooms = response,
        error: (error) => this.errorMessage = error.error.error
      });
    this.userService
      .getAllUsers(0, 10000, ['ROLE_ADMIN', 'ROLE_TEACHER'])
      .subscribe((value) => {
        this.teachers = value.content;
      });

    this.teachersGroup = this.formBuilder.group({
      teacherId: ['', Validators.required],
    });

    this.teachersGroup.controls.teacherId.valueChanges.subscribe(
      (teacherId) => {
        this.updateScheduleList(teacherId);
      }
    );
    const date = new Date();
    const day = date.getDay();
    const diffToSunday = day === 0 ? -7 : 0 - day;
    const sunday = new Date(date);
    sunday.setDate(date.getDate() + diffToSunday);
    this.startDate = sunday.toISOString().split('T')[0];
    this.dateStartDate = new Date(this.startDate);
    this.dateEndDate = new Date(this.endDate);
    date.setDate(sunday.getDate() + 6);
    this.endDate = date.toISOString().split('T')[0];
    this.setScheduleDates();
  }

  private updateScheduleList(teacherId: number | null): void {
    this.loading = true;
    this.scheduleService.getAllByTeacher(teacherId).subscribe({
      next: (schedules) => {
        this.lessonSchedules = schedules;
      },
      error: (error) => {
        this.errorMessage = error.error.error;
      },
      complete: () => (this.loading = false),
    });
  }

  public openEditModal(schedule: LessonSchedule | Lesson, teacherId: number): void {
    this.currentDialog = this.modalService.open(LessonsScheduleEditComponent, {
      scrollable: true,
      windowClass: 'modal-lesson-schedule-edit',
      size: 'lg',
      centered: true,
    });

    this.currentDialog.componentInstance.lessonSchedule = schedule;
    this.currentDialog.componentInstance.rooms = this.rooms;
    this.currentDialog.componentInstance.teacherId = teacherId;
    this.currentDialog.componentInstance.teachers = this.teachers;

    this.currentDialog.result.then(
      (result) => {
        if (result instanceof FormGroup) {
          const edited = new LessonSchedule(result.value);
          this.scheduleService.createOrEdit(edited).subscribe({
            next: () => this.refreshLessonsData(teacherId),
            error: (error) => {
              if (error.error && error.error.message) {
                this.errorMessage = error.error.message;
              } else {
                this.errorMessage = 'An unexpected error occurred';
              }
            },
          });
        }
      },
      (error) => {
        console.log('Dialog close error: ' + error);
      }
    );
  }

  public openDeleteConfirmModal(lessonSchedule: LessonSchedule): void {
    this.currentDialog = this.modalService.open(ConfirmDialogComponent, {
      scrollable: true,
      windowClass: 'modal-lesson-schedule-delete-confirm',
      size: 'md',
      centered: true,
    });

    this.currentDialog.componentInstance.message =
      'Are you sure that you want delete lessons schedule with id ' +
      lessonSchedule.id +
      '?';

    this.currentDialog.result.then(
      (result) => {
        if (result.status === 'confirmed') {
          this.scheduleService.delete(lessonSchedule.id).subscribe({
            next: () => this.refreshLessonsData(this.teachersGroup.controls.teacherId.value),
            error: (error) => (this.errorMessage = error.error.error),
          });
        }
      },
      (error) => {
        console.log('Dialog close error: ' + error);
      }
    );
  }

  private getDaysOfWeekInRange(dates: Date[]): string[] {
    const days = new Set<string>();
    dates.map(date => days.add(this.weekDays[date.getDay()]));
    return Array.from(days);
  }

  private groupByTimeAndRoom(data: Lesson[]): any {
    const daysOfWeek = this.getDaysOfWeekInRange(this.weekDates);
    const filteredLessons = data.filter(lesson =>
      daysOfWeek.includes(lesson.dayOfWeek)
    );
    const uniqueTimes = [...new Set(filteredLessons.map(l => l.startTime))].sort();
    return uniqueTimes.map(time => {
      const lessonsAtTime = filteredLessons.filter(l => l.startTime === time);

      const groupedByRoom = new Map<number | null, Lesson[]>();
      lessonsAtTime.forEach(lesson => {
        if (!groupedByRoom.has(lesson.roomId)) {
          groupedByRoom.set(lesson.roomId, []);
        }
      });

      const rooms = Array.from(groupedByRoom.entries()).map(([roomId, lessons]) => {

/*        const lessonsMap = new Map<number | null, Lesson>();

        lessons.forEach((lesson: Lesson) => {
          lessonsMap.set()
        });*/

        return {
          roomId,
          entries: lessons,
        };
      }).sort((a, b) => {
        if (a.roomId === null) {
          return 1;
        }
        if (b.roomId === null) {
          return -1;
        }
        return 0;
      });

      return {
        startTime: time,
        rooms,
        rowspan: rooms.length,
      };
    });
  }

  public setScheduleDates(): void {
    const start = (new Date(this.startDate)).getTime();
    const end = (new Date(this.endDate)).getTime();
    if (start && end && start <= end) {
      this.generateDatesRange();
      this.scheduleService
        .getScheduledLessonsByDateRange(this.startDate, this.endDate)
        .subscribe((response) => {
          this.lessonsScheduleByDates = this.groupByTimeAndRoom(response);
          this.rawEvents = response;
        });
      this.dateStartDate = new Date(this.startDate);
      this.dateEndDate = new Date(this.endDate);
    }
  }

  private generateDatesRange(): void {
    this.weekDates = [];
    const current = new Date(this.startDate);
    const end = new Date(this.endDate);
    current.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    while (current <= end) {
      this.weekDates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
  }

  public changeViewMode(): void {
    this.viewMode = Number(!this.viewMode);
  }

  private refreshLessonsData(teacherId: number | null): void {
    this.updateScheduleList(teacherId);
    this.setScheduleDates();
  }

}
